import { HorizontalDivider } from '@ctw/shared/components/horizontal-divider';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { ReactHtmlRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/react/react-html-renderer';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { CdaDocument } from '@ctw/shared/utils/cda/types';
import { getBffApiBaseUrl } from '@ctw/shared/utils/environments';
import { tw } from '@ctw/shared/utils/tailwind';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface RelatedDocumentNarrativesProps {
  binaryId: string;
}

export const RelatedDocumentNarratives = ({ binaryId }: RelatedDocumentNarrativesProps) => {
  const { isLoading, isError, data } = useNarrativeForBinary({ binaryId });
  const sections = data?.cda?.content?.sections ?? [];
  const id = `related-document-narrative-${binaryId}`;

  if (isLoading) {
    return <LoadingSpinner message="Loading document..." centered={true} />;
  }

  if (isError) {
    return (
      <div className={tw`flex items-center gap-2 px-2 py-3 text-content-subtle`}>
        <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-5 w-5`} />
        <div className={tw`font-medium text-[1rem] tracking-wide`}>
          Unable to load this document
        </div>
      </div>
    );
  }

  if (sections.length === 0) {
    return null;
  }

  return (
    <div id={id} className={tw`flex flex-col gap-1 px-2 py-3`}>
      {sections.map((section, index) => (
        <div key={section.id} className={tw`flex flex-col gap-1`}>
          {index !== 0 && <HorizontalDivider />}
          <div className={tw`font-medium text-[0.9rem]`}>{section.title}</div>
          <div className={tw`text-sm`}>
            <ReactHtmlRenderer
              id={section.id}
              parsedHtmlNodes={section.parsedHtmlNodes ?? []}
              emptyState={undefined}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

interface BinaryNarrativesResponse {
  title?: string;
  contentType: string;
  cda?: CdaDocument;
}

interface UseBinaryNarrativeArgs {
  binaryId: string;
}

export const useNarrativeForBinary = ({ binaryId }: UseBinaryNarrativeArgs) =>
  usePatientQuery({
    enabled: binaryId !== '',
    queryId: 'binary-narrative',
    queryKey: [binaryId],
    queryFn: async ({ requestContext, telemetry }): Promise<BinaryNarrativesResponse> => {
      const response = await fetch(`${getBffApiBaseUrl(requestContext.env)}/v1/binary/narratives`, {
        headers: {
          Authorization: `Bearer ${requestContext.authToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({ binaryId }),
      });

      const failWith = failBinaryNarrativeFetch(telemetry, binaryId, response);

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        return failWith(body.slice(0, 500) || 'no response body');
      }

      const responseJson = await response.json().catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        return failWith(`response was not valid JSON: ${message}`);
      });

      if (!responseJson?.data?.attributes) {
        return failWith('response was missing data.attributes');
      }

      return responseJson.data.attributes;
    },
  });

const failBinaryNarrativeFetch =
  (telemetry: ReturnType<typeof useTelemetry>, binaryId: string, response: Response) =>
  (detail: string): never => {
    const error = new Error(
      `Failed to fetch binary narratives: ${response.status} ${response.statusText} — ${detail}`,
    );
    telemetry.trackError({
      message: 'Failed to fetch binary narratives server-side',
      error,
      context: { binaryId },
    });
    throw error;
  };
